import React, { useState } from 'react';
import { Card, Table, Form, DatePicker, Button } from 'antd';
import { useTable } from '@/hooks/useTable';
import dayjs from 'dayjs';
import { getUserArtworkStatistic } from '@/api/user';
import type { UserArtworkStatisticParams } from '@/api/user';

const { RangePicker } = DatePicker;

// 获取上周的开始和结束时间
const getLastWeekRange = () => {
  const today = dayjs();
  const lastWeekMonday = today.subtract(1, 'week').startOf('week').add(1, 'day');
  const lastWeekSunday = lastWeekMonday.add(6, 'day');
  return [lastWeekMonday, lastWeekSunday];
};

const UserRegisterPage: React.FC = () => {
  const [form] = Form.useForm();
  const [lastWeekStart, lastWeekEnd] = getLastWeekRange();
  
  // 设置初始搜索参数，包含默认时间范围
  const initialParams: UserArtworkStatisticParams = {
    createTimeStart: lastWeekStart.startOf('day').valueOf(),
    createTimeEnd: lastWeekEnd.endOf('day').valueOf(),
  };
  
  const [searchParams, setSearchParams] = useState<UserArtworkStatisticParams>(initialParams);

  // 初始化表单默认值
  React.useEffect(() => {
    form.setFieldsValue({ 
      timeRange: [lastWeekStart, lastWeekEnd] 
    });
  }, []);

  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    handleTableChange,
    updateParams,
  } = useTable({
    queryKey: 'user-register-list',
    queryFn: async (current, size, params) => {
      const response = await getUserArtworkStatistic({
        currentPage: current,
        pageSize: size,
        ...params,
      });
      return response.data;
    },
    defaultParams: initialParams,
  });

  const handleSearch = async () => {
    const values = await form.validateFields();
    const params: UserArtworkStatisticParams = {};

    if (values.timeRange) {
      const [start, end] = values.timeRange;
      params.createTimeStart = start.startOf('day').valueOf();
      params.createTimeEnd = end.endOf('day').valueOf();
    }

    setSearchParams(params);
    updateParams(params);
  };

  const handleReset = () => {
    form.setFieldsValue({ timeRange: [lastWeekStart, lastWeekEnd] });
    setSearchParams(initialParams);
    updateParams(initialParams);
  };

  const columns = [
    {
      title: '用户ID',
      dataIndex: 'suppId',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      render: (email: string | null) => email || '-',
    },
    {
      title: '作品数量',
      dataIndex: 'artworkCount',
    },
    {
      title: '注册时间',
      dataIndex: 'createTime',
      render: (time: number) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <Card>
      <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item name="timeRange" label="注册时间">
          <RangePicker 
            showTime={false} 
            format="YYYY-MM-DD"
          />
        </Form.Item>
        <Form.Item>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={handleSearch}>搜索</Button>
            <Button onClick={handleReset}>重置</Button>
          </div>
        </Form.Item>
      </Form>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="suppId"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
        }}
        loading={isLoading}
        onChange={handleTableChange}
      />
    </Card>
  );
};

export default UserRegisterPage;
